import React, { useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../../context/Web3Context";
import { formatUSDT, formatUSDTCompact, bytes32ToString, timeAgo, shortAddress } from "../../utils/format";
import { DAILY_CLUB_NAMES, ADMIN_ACTIVITY_TYPES } from "../../config/constants";
import { useAdminLog } from "../../hooks/useAdminLog";
import Button from "../../components/Common/Button";
import { Card } from "../../components/Common/Card";

const REG_TYPE_ADMIN_VIRTUAL = 2;
const LOG_PAGE_SIZE = 10;

function AdminHistory() {
  const { account, getContract, isOwner } = useWeb3();
  const [loading, setLoading] = useState(true);
  const [logPage, setLogPage] = useState(0);
  const [summary, setSummary] = useState({
    totalUsers: 0,
    totalInvestment: ethers.BigNumber.from(0),
    todayUsers: 0,
    todayInvestment: ethers.BigNumber.from(0),
  });
  const [dist, setDist] = useState({
    daily: [0, 0, 0, 0, 0, 0, 0].map(() => ethers.BigNumber.from(0)),
    dailyCount: [0, 0, 0, 0, 0, 0, 0],
  });
  const [recentClaims, setRecentClaims] = useState([]);
  const [virtualRegs, setVirtualRegs] = useState([]);
  const [pageDaily, setPageDaily] = useState(0);
  const cancelRef = useRef(false);

  // On-chain admin log — Storage.getAdminActivities(). Newest last from
  // contract, so we reverse to newest-first before rendering.
  const { entries: adminActsRaw, totalCount: logTotalCount, loading: logLoading } =
    useAdminLog({ actor: account, limit: 100, autoRefresh: 30 });
  const adminActs = [...adminActsRaw].reverse();
  const logTotalPages = Math.max(1, Math.ceil(adminActs.length / LOG_PAGE_SIZE));
  useEffect(() => {
    if (logPage > logTotalPages - 1) setLogPage(0);
  }, [logPage, logTotalPages]);
  const pagedActs = adminActs.slice(logPage * LOG_PAGE_SIZE, (logPage + 1) * LOG_PAGE_SIZE);

  useEffect(() => {
    cancelRef.current = false;
    const load = async () => {
      const contract = getContract();
      if (!contract) { setLoading(false); return; }

      setLoading(true);
      try {
        // 1) Fast: getStats() gives us all overview numbers in one call
        try {
          const s = await contract.getStats();
          if (!cancelRef.current) {
            setSummary({
              totalUsers: s.totalUsers_.toNumber ? s.totalUsers_.toNumber() : Number(s.totalUsers_.toString()),
              totalInvestment: s.totalInvestment_,
              todayUsers: s.todayUsers.toNumber ? s.todayUsers.toNumber() : Number(s.todayUsers.toString()),
              todayInvestment: s.todayInvestment,
            });
          }
        } catch {}

        // 2) Distribution + recent claims + virtual regs — only DailyClubClaimed
        // + UserRegistered exist on the new contract (weekly/monthly removed).
        // Wrap each queryFilter separately so one failure doesn't kill the others.
        const safeQuery = async (fn) => {
          try {
            return await contract.queryFilter(fn(), 0, "latest");
          } catch { return []; }
        };
        const [dailyEvts, regEvts] = await Promise.all([
          contract.filters.DailyClubClaimed ? safeQuery(() => contract.filters.DailyClubClaimed()) : Promise.resolve([]),
          contract.filters.UserRegistered ? safeQuery(() => contract.filters.UserRegistered()) : Promise.resolve([]),
        ]);

        const dailyTotals = [0, 0, 0, 0, 0, 0, 0].map(() => ethers.BigNumber.from(0));
        const dailyCounts = [0, 0, 0, 0, 0, 0, 0];
        for (const e of dailyEvts) {
          const club = e.args.clubId.toNumber() - 1;
          if (club >= 0 && club < 7) {
            dailyTotals[club] = dailyTotals[club].add(e.args.amount);
            dailyCounts[club]++;
          }
        }

        const allClaims = dailyEvts
          .map((e) => ({
            type: "daily",
            userId: e.args.userId.toString(),
            club: e.args.clubId.toNumber(),
            amount: e.args.amount,
            block: e.blockNumber,
          }))
          .sort((a, b) => b.block - a.block)
          .slice(0, 30);

        const virtuals = regEvts
          .filter((e) => e.args.registrationType === REG_TYPE_ADMIN_VIRTUAL)
          .map((e) => ({
            userId: e.args.userId.toString(),
            wallet: e.args.wallet,
            referrerId: e.args.referrerId.toString(),
            serialNumber: e.args.serialNumber.toString(),
            name: bytes32ToString(e.args.name),
            block: e.blockNumber,
          }))
          .sort((a, b) => b.block - a.block);

        if (!cancelRef.current) {
          setDist({
            daily: dailyTotals,
            dailyCount: dailyCounts,
          });
          setRecentClaims(allClaims);
          setVirtualRegs(virtuals);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelRef.current) setLoading(false);
      }
    };
    load();
    return () => { cancelRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Admin action log is now fetched via useAdminLog hook from PHP endpoint)

  if (loading) {
    return (
      <div className="empty-state">
        <span className="spinner spinner-lg" />
        <div className="empty-state-text mt-md">Fetching on-chain history...</div>
      </div>
    );
  }

  const grandTotal = dist.daily.reduce((s, v) => s.add(v), ethers.BigNumber.from(0));
  const grandCount = dist.dailyCount.reduce((s, v) => s + v, 0);

  const claimsPerPage = 10;
  const totalClaimPages = Math.max(1, Math.ceil(recentClaims.length / claimsPerPage));
  const paginatedClaims = recentClaims.slice(pageDaily * claimsPerPage, (pageDaily + 1) * claimsPerPage);

  return (
    <div>
      {/* ============ Summary Stats (from getStats()) ============ */}
      <div className="section-header" style={{ marginTop: 0 }}>
        <div className="section-title">Overview</div>
      </div>
      <div className="stat-grid mb-md">
        <div className="stat-card">
          <div className="stat-card-icon">👥</div>
          <div className="stat-card-label">Total Users</div>
          <div className="stat-card-value" style={{ color: "var(--cyan)" }}>
            {summary.totalUsers.toLocaleString()}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon">💰</div>
          <div className="stat-card-label">Total Investment</div>
          <div className="stat-card-value" style={{ color: "var(--success)" }}>
            ${formatUSDTCompact(summary.totalInvestment)}
          </div>
        </div>
      </div>
      <div className="stat-grid mb-lg">
        <div className="stat-card" style={{ borderColor: "rgba(139, 92, 246, 0.5)" }}>
          <div className="stat-card-icon" style={{ color: "var(--violet-bright)" }}>✨</div>
          <div className="stat-card-label">Today New Users</div>
          <div className="stat-card-value" style={{ color: "var(--violet-bright)" }}>
            +{summary.todayUsers}
          </div>
          <div className="stat-card-sub">since UTC 00:00</div>
        </div>
        <div className="stat-card" style={{ borderColor: "rgba(236, 72, 153, 0.5)" }}>
          <div className="stat-card-icon" style={{ color: "var(--magenta-bright)" }}>⚡</div>
          <div className="stat-card-label">Today Investment</div>
          <div className="stat-card-value" style={{ color: "var(--magenta-bright)" }}>
            ${formatUSDTCompact(summary.todayInvestment)}
          </div>
          <div className="stat-card-sub">since UTC 00:00</div>
        </div>
      </div>

      {/* Total distributed via clubs */}
      <Card glow className="mb-lg" style={{ textAlign: "center" }}>
        <div className="stat-card-label">Total Royalties Claimed</div>
        <div style={{
          fontFamily: "'Sora', 'Inter', sans-serif",
          fontSize: 34, fontWeight: 700,
          background: "var(--grad-cyber)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "-0.02em",
          margin: "6px 0",
          fontVariantNumeric: "tabular-nums",
        }}>
          ${formatUSDT(grandTotal)}
        </div>
        <div className="stat-card-sub">{grandCount} claim{grandCount !== 1 ? "s" : ""}</div>
      </Card>

      {/* Daily distribution per rank */}
      <div className="section-header">
        <div className="section-title">Royalty Distribution by Rank</div>
      </div>
      <Card className="mb-lg">
        {[1, 2, 3, 4, 5, 6, 7].map((clubId) => {
          const amt = dist.daily[clubId - 1];
          const count = dist.dailyCount[clubId - 1];
          return (
            <div
              key={clubId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 0",
                borderBottom: clubId < 7 ? "1px solid var(--border)" : "none",
              }}
            >
              <span className="badge badge-cyan" style={{ minWidth: 22, justifyContent: "center", fontSize: 10 }}>
                {clubId}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                  {DAILY_CLUB_NAMES[clubId]}
                </div>
                <div style={{ fontSize: 10, color: "var(--text-muted)" }}>
                  {count} claim{count !== 1 ? "s" : ""}
                </div>
              </div>
              <div className="mono" style={{ fontSize: 13, fontWeight: 700, color: "var(--cyan)", fontVariantNumeric: "tabular-nums" }}>
                ${formatUSDT(amt)}
              </div>
            </div>
          );
        })}
      </Card>

      {/* Recent claims */}
      <div className="section-header">
        <div className="section-title">Recent Royalty Claims</div>
        <span className="badge badge-cyan">{recentClaims.length}</span>
      </div>
      {recentClaims.length === 0 ? (
        <Card><div className="empty-state-text">No royalty claims yet</div></Card>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {paginatedClaims.map((c, i) => {
              const typeName = DAILY_CLUB_NAMES[c.club] || `Royalty ${c.club}`;
              return (
                <div key={i} className="list-item">
                  <div className="list-item-icon">☀</div>
                  <div className="list-item-body">
                    <div className="list-item-title">{typeName}</div>
                    <div className="list-item-sub">User #{c.userId} · Block {c.block}</div>
                  </div>
                  <div className="list-item-value mono">${formatUSDT(c.amount)}</div>
                </div>
              );
            })}
          </div>
          {totalClaimPages > 1 && (
            <div className="pagination">
              <Button variant="ghost" size="sm" disabled={pageDaily === 0} onClick={() => setPageDaily((p) => Math.max(0, p - 1))}>‹ Prev</Button>
              <span className="pagination-info">Page {pageDaily + 1} / {totalClaimPages}</span>
              <Button variant="ghost" size="sm" disabled={pageDaily >= totalClaimPages - 1} onClick={() => setPageDaily((p) => Math.min(totalClaimPages - 1, p + 1))}>Next ›</Button>
            </div>
          )}
        </>
      )}

      {/* Virtual registrations */}
      <div className="section-header">
        <div className="section-title">Virtual Registrations</div>
        <span className="badge badge-violet">{virtualRegs.length}</span>
      </div>
      {virtualRegs.length === 0 ? (
        <Card><div className="empty-state-text">No virtual registrations yet</div></Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {virtualRegs.slice(0, 20).map((v, i) => (
            <div key={i} className="list-item">
              <div className="list-item-icon">◇</div>
              <div className="list-item-body">
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span className="list-item-title" style={{ overflow: "visible", whiteSpace: "normal" }}>
                    {v.name || "—"}
                  </span>
                  <span className="badge badge-violet" style={{ fontSize: 9 }}>VIRTUAL</span>
                </div>
                <div className="list-item-sub mono">
                  #{v.userId} · sponsor #{v.referrerId} · {shortAddress(v.wallet, 4)}
                </div>
              </div>
              <div style={{ fontSize: 10, color: "var(--text-muted)", textAlign: "right" }}>
                SN {v.serialNumber}
              </div>
            </div>
          ))}
          {virtualRegs.length > 20 && (
            <div className="empty-state-text" style={{ padding: 8, fontSize: 11 }}>
              Showing latest 20 of {virtualRegs.length}
            </div>
          )}
        </div>
      )}

      {/* Admin activity log — pure on-chain from Storage.getAdminActivities() */}
      <div className="section-header">
        <div className="section-title">
          {isOwner ? "Owner Action Log" : "Admin Action Log"}
        </div>
        <span className={`badge ${isOwner ? "badge-magenta" : "badge-cyan"}`}>
          {logTotalCount || adminActs.length}
        </span>
      </div>

      <div style={{
        background: "rgba(0, 229, 255, 0.05)",
        border: "1px solid var(--cyan-border)",
        borderRadius: 10,
        padding: 10,
        marginBottom: 10,
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 11,
        color: "var(--text-secondary)",
      }}>
        <span style={{ color: "var(--cyan)", fontSize: 14 }}>⛓</span>
        <span>
          Sourced directly from{" "}
          <code style={{ color: "var(--cyan)" }}>Storage.getAdminActivities()</code>.
          Contract keeps the latest 100 actions per admin address.
        </span>
      </div>

      {logLoading && adminActs.length === 0 ? (
        <Card><div className="empty-state-text"><span className="spinner" /> Loading on-chain log…</div></Card>
      ) : adminActs.length === 0 ? (
        <Card>
          <div className="empty-state-text">
            No actions logged for this address yet
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-muted)" }}>
              Perform any admin action (block user, topup, virtual register, wallet update, etc.)
              and it will appear here immediately after confirmation.
            </div>
          </div>
        </Card>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {pagedActs.map((a, i) => <AdminActRow key={logPage * LOG_PAGE_SIZE + i} a={a} />)}
          </div>
          {logTotalPages > 1 && (
            <div className="pagination" style={{ marginTop: 10 }}>
              <Button variant="ghost" size="sm" disabled={logPage === 0}
                onClick={() => setLogPage((p) => Math.max(0, p - 1))}>‹ Prev</Button>
              <span className="pagination-info">
                Page {logPage + 1} / {logTotalPages} · {adminActs.length} shown
              </span>
              <Button variant="ghost" size="sm" disabled={logPage >= logTotalPages - 1}
                onClick={() => setLogPage((p) => Math.min(logTotalPages - 1, p + 1))}>Next ›</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Contract activity codes → nice labels. Extends ADMIN_ACTIVITY_TYPES with
// migration codes 40-48 (not in the shared constants file yet).
const CODE_META = {
  ...ADMIN_ACTIVITY_TYPES,
  40: { label: "Set Migrator",           icon: "⚙",  color: "#a78bfa" },
  41: { label: "Locked Migration",       icon: "🔒", color: "#fbbf24" },
  42: { label: "Migrated: Register",     icon: "📥", color: "#00e5ff" },
  43: { label: "Migrated: Activate",     icon: "📥", color: "#7df9ff" },
  44: { label: "Migrated: Retopup",      icon: "📥", color: "#a78bfa" },
  45: { label: "Migrated: Direct Count", icon: "📥", color: "#22ffa0" },
  46: { label: "Migrated: Income",       icon: "📥", color: "#ec4899" },
  47: { label: "Migrated: Claimed",      icon: "📥", color: "#fbbf24" },
  48: { label: "Migration State",        icon: "📥", color: "#a78bfa" },
};

// For each activity code, decide how the relatedId + amount fields
// should be interpreted in the subline.
function describeSubline(code, a) {
  const id = a.relatedId;
  const idNum = id && id !== "0" ? Number(id) : 0;
  switch (code) {
    case 20: // Register: relatedId = new userId
      return idNum ? `New user #${idNum}` : "";
    case 21: // Activate: relatedId = userId (packageId in amount slot? contract passes packageId in a different slot)
    case 22: // Retopup
      // Contract stores: netAmount = 0, amount = 0, relatedId = userId
      return idNum ? `User #${idNum}` : "";
    case 23: // Block
    case 24: // Unblock
    case 25: // Update name
    case 26: // Update wallet
      return idNum ? `User #${idNum}` : "";
    case 27: // Set royalty round
      return DAILY_CLUB_NAMES[idNum] ? `${DAILY_CLUB_NAMES[idNum]} royalty` : (idNum ? `Royalty ${idNum}` : "");
    case 31: // Update team wallet
    case 32: // Update missing wallet
      return "Wallet rotated";
    case 33: // Emergency withdraw — amount = withdrawn amount
      return "";
    case 40: return "Migrator address changed";
    case 41: return "Migration permanently locked";
    default: return idNum ? `Ref #${idNum}` : "";
  }
}

function AdminActRow({ a }) {
  const code = Number(a.activityType || 0);
  const meta = CODE_META[code] || { label: `Type ${code}`, icon: "•", color: "var(--text-muted)" };
  const amountShown = a.amount && a.amount.gt && a.amount.gt(0)
    ? `$${formatUSDT(a.amount)}`
    : (a.netAmount && a.netAmount.gt && a.netAmount.gt(0) ? `$${formatUSDT(a.netAmount)}` : "");
  const sub = describeSubline(code, a);

  return (
    <div className="list-item">
      <div className="list-item-icon" style={{ color: meta.color, borderColor: meta.color + "55" }}>
        {meta.icon}
      </div>
      <div className="list-item-body">
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <div className="list-item-title" style={{ color: meta.color }}>{meta.label}</div>
        </div>
        <div className="list-item-sub">
          {timeAgo(a.timestamp)}
          {sub ? ` · ${sub}` : ""}
        </div>
      </div>
      {amountShown && (
        <div className="list-item-value mono" style={{ color: meta.color, fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
          {amountShown}
        </div>
      )}
    </div>
  );
}

export default AdminHistory;
